import { Component, type ReactNode, type ErrorInfo, type ComponentType } from 'react'
import * as Sentry from '@sentry/react'
import { Button } from '@/components/ui/button'
import { AlertTriangle, Home, RefreshCw, MessageCircle } from 'lucide-react'
import { isSentryInitialised } from '@/lib/monitoring/sentry'

// ---------------------------------------------------------------------------
// Crash reporting — forwards errors to Sentry when initialised
// ---------------------------------------------------------------------------

interface ErrorContext {
  componentStack?: string
  [key: string]: unknown
}

/**
 * Report an error to Sentry. Falls back to console logging when
 * Sentry is not initialised (e.g. local development).
 */
export function reportError(error: unknown, context?: ErrorContext): void {
  if (isSentryInitialised()) {
    Sentry.captureException(error, { extra: context as Record<string, unknown> })
  }

  if (import.meta.env.DEV) {
    console.error('[Zero Platform] Error reported:', error, context)
  }
}

// ---------------------------------------------------------------------------
// Error boundary
// ---------------------------------------------------------------------------

interface ErrorBoundaryProps {
  children: ReactNode
  /** Optional custom fallback UI. Can be a ReactNode or a render function. */
  fallback?: ReactNode | ((props: { error: Error; reset: () => void }) => ReactNode)
  /** Optional callback invoked whenever an error is caught. */
  onError?: (error: Error, info: ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  resetCount: number
}

const MAX_RESETS = 3

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, resetCount: 0 }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, { componentStack: info.componentStack ?? undefined })
    this.props.onError?.(error, info)
  }

  private handleReset = (): void => {
    const nextCount = this.state.resetCount + 1
    if (nextCount >= MAX_RESETS) {
      // Too many resets -- stay on the error screen permanently
      this.setState({ resetCount: nextCount })
      return
    }
    this.setState({ hasError: false, error: null, resetCount: nextCount })
  }

  private handleReload = (): void => {
    window.location.reload()
  }

  private handleGoHome = (): void => {
    window.location.href = '/'
  }

  private handleReportIssue = (): void => {
    window.location.href = 'mailto:support@zeroshooting.com?subject=Bug%20Report'
  }

  render(): ReactNode {
    if (!this.state.hasError || !this.state.error) {
      return this.props.children
    }

    const isPermanent = this.state.resetCount >= MAX_RESETS

    // ---- Custom fallback ----
    if (this.props.fallback) {
      if (typeof this.props.fallback === 'function') {
        return (this.props.fallback as (props: { error: Error; reset: () => void }) => ReactNode)({
          error: this.state.error,
          reset: this.handleReset,
        })
      }
      return this.props.fallback
    }

    // ---- Default fallback UI ----
    const sanitisedMessage = import.meta.env.DEV
      ? this.state.error.message
      : 'An unexpected error occurred. Please try again or contact support.'

    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          {/* Branding */}
          <p className="mb-6 text-sm font-medium tracking-wide text-gray-400">Zero Platform</p>

          {/* Icon */}
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-7 w-7 text-red-600" />
          </div>

          {/* Heading */}
          <h1 className="mb-2 text-2xl font-bold text-gray-900">Something went wrong</h1>
          <p className="mb-6 text-sm text-gray-600">{sanitisedMessage}</p>

          {isPermanent && (
            <p className="mb-4 text-xs text-red-600">
              This error keeps occurring. Please reload the page or contact support.
            </p>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            {!isPermanent && (
              <Button variant="primary" onClick={this.handleReset}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            )}
            {isPermanent && (
              <Button variant="primary" onClick={this.handleReload}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh Page
              </Button>
            )}
            <Button variant="outline" onClick={this.handleGoHome}>
              <Home className="mr-2 h-4 w-4" />
              Go Home
            </Button>
            <Button variant="ghost" onClick={this.handleReportIssue}>
              <MessageCircle className="mr-2 h-4 w-4" />
              Report Issue
            </Button>
          </div>
        </div>
      </div>
    )
  }
}

// ---------------------------------------------------------------------------
// Higher-order component wrapper
// ---------------------------------------------------------------------------

interface WithErrorBoundaryOptions {
  fallback?: ErrorBoundaryProps['fallback']
  onError?: ErrorBoundaryProps['onError']
}

export function withErrorBoundary<P extends object>(
  WrappedComponent: ComponentType<P>,
  options: WithErrorBoundaryOptions = {},
): ComponentType<P> {
  function WithErrorBoundaryWrapper(props: P) {
    return (
      <ErrorBoundary fallback={options.fallback} onError={options.onError}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    )
  }

  WithErrorBoundaryWrapper.displayName = `withErrorBoundary(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`

  return WithErrorBoundaryWrapper
}
